/**
 * <quantity-tiers> — Staffelrabatte tier selector (D-19.5).
 *
 * Rebuild of live's ACTIVE legacy-prefixed tier-selector element (live's
 * assets/pdp.js — theme.liquid loads only pdp.js; the older webpack build of
 * the same element is an unreferenced asset). Behavior ported 1:1:
 *
 *  - on connect, sync to the server-marked current option (Liquid marks the
 *    first tier), re-checked once at 50 ms — live's `_syncSelectedOption`;
 *  - clicking a tier moves the `bulk-price__option--current` class, then
 *    updates: the price header (regular ↔ on-sale; compare-at = single price
 *    × quantity; savings badge when present), the shipping free/paid toggle,
 *    the unit-price line, the quantity row label, the hidden quantity input
 *    the section renders in place of the stepper on tier PDPs (D-21 item
 *    44; value + bubbling `change` for any form-associated consumers — the
 *    product form itself is untouched: the standard form submits the
 *    selected quantity, R-01), and the gallery image (high-res srcset
 *    rebuild → `quantity-tiers:image-change`, handled by media-gallery.js).
 *
 * Pack-list layout (block setting `layout: pack_list`): the cards carry
 * role="radio" inside a radiogroup, so selection also moves aria-checked and
 * the roving tabindex, and the arrow / Home / End / Space / Enter keys select
 * like native radios. Classic cards carry no role and are untouched.
 *
 * Deliberately NOT ported (piece report, DISCLOSED-GAPS):
 *  - `ed:select-bulk-option` / `ed:eab-price-ready` (external A/B hooks with
 *    no in-theme dispatcher; Intelligems is disabled per D-14) and the
 *    `properties[_eabprice]` hidden-input write (R-01: no new cart-write
 *    patterns);
 *  - `ed:memberr-cb:variant-change` (the memberr block is schema-only and
 *    disabled on every stored template).
 *
 * Self-contained money formatting from <body data-money-format> — the same
 * pattern as free-shipping-status.js (§5: no first-party import graph).
 */
if (!customElements.get('quantity-tiers')) {
  class QuantityTiers extends HTMLElement {
    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('.bulk-price__option'));
      this.quantityLabels = Array.from(this.querySelectorAll('.bulk-price__quantity[data-index]'));
      this.unitPriceElement = this.querySelector('[data-unit-price]');
      this.quantityInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);

      this.originalPrice = this.toCents(this.dataset.originalPrice);
      this.originalUnitPrice = this.dataset.originalUnitPrice || '';
      this.moneyFormat = document.body.getAttribute('data-money-format') || '€{{ amount_with_comma_separator }}';

      this.boundOptionClick = this.onOptionClick.bind(this);
      this.boundOptionKeydown = this.onOptionKeydown.bind(this);
      this.options.forEach((option) => {
        option.addEventListener('click', this.boundOptionClick);
        if (this.isRadio(option)) option.addEventListener('keydown', this.boundOptionKeydown);
      });

      this.syncSelectedOption();
      setTimeout(() => this.syncSelectedOption(), 50);
    }

    disconnectedCallback() {
      this.options.forEach((option) => {
        option.removeEventListener('click', this.boundOptionClick);
        option.removeEventListener('keydown', this.boundOptionKeydown);
      });
    }

    syncSelectedOption() {
      if (!this.options.length) return;
      const current = this.querySelector('.bulk-price__option--current') || this.options[0];
      this.onOptionClick({ currentTarget: current });
    }

    onOptionClick(event) {
      this.options.forEach((option) => {
        const selected = event.currentTarget === option;
        if (this.isRadio(option)) {
          option.setAttribute('aria-checked', String(selected));
          option.tabIndex = selected ? 0 : -1;
        }
        if (selected) {
          option.classList.add('bulk-price__option--current');
          this.selectItem(option);
        } else {
          option.classList.remove('bulk-price__option--current');
        }
      });
    }

    onOptionKeydown(event) {
      const index = this.options.indexOf(event.currentTarget);
      const last = this.options.length - 1;
      let next;
      switch (event.key) {
        case ' ':
        case 'Enter':
          next = index;
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          next = index === last ? 0 : index + 1;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          next = index === 0 ? last : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.onOptionClick({ currentTarget: this.options[next] });
      this.options[next].focus();
    }

    isRadio(option) {
      return option.getAttribute('role') === 'radio';
    }

    selectItem(option) {
      const price = this.toCents(option.dataset.price);
      const quantity = this.parseQuantity(option.dataset.quantity);
      const comparePrice = this.originalPrice * quantity;

      this.updatePrice(price, comparePrice);
      this.updateShippingInfo(price);
      if (this.unitPriceElement) this.updateUnitPrice(option.dataset.unitPriceValue);
      this.updateQuantityLabel(parseInt(option.dataset.index, 10));
      this.updateQuantityInput(quantity);
      this.updateProductImage(option);
    }

    updatePrice(price, comparePrice) {
      const priceContainer = this.querySelector('.price');
      const regularContainer = this.querySelector('.price__regular');
      const saleContainer = this.querySelector('.price__sale');
      if (!priceContainer || !regularContainer || !saleContainer) return;

      if (comparePrice > price) {
        priceContainer.classList.add('price--on-sale');
        const saleItem = saleContainer.querySelector('.price-item--sale');
        const compareItem = saleContainer.querySelector('.price-item--regular');
        const savingsBadge = saleContainer.querySelector('.total-savings-badge');
        if (saleItem) saleItem.innerText = this.formatMoney(price);
        if (compareItem) compareItem.innerText = this.formatMoney(comparePrice);
        if (savingsBadge) savingsBadge.innerText = this.formatMoney(comparePrice - price);
      } else {
        priceContainer.classList.remove('price--on-sale');
        const regularItem = regularContainer.querySelector('.price-item--regular');
        if (regularItem) regularItem.innerText = this.formatMoney(price);
      }
    }

    updateShippingInfo(price) {
      const note = this.querySelector('.shipping-note-toggle');
      if (!note) return;
      const freeText = note.querySelector('[data-shipping-text="free"]');
      const paidText = note.querySelector('[data-shipping-text="paid"]');
      if (!freeText || !paidText) return;
      const eligibleCountry = note.dataset.eligibleCountry === 'true';
      const freeShippingProduct = note.dataset.freeShippingProduct === 'true';
      const threshold = parseInt(note.dataset.freeShippingThreshold, 10) || 0;
      const showFree = eligibleCountry && (freeShippingProduct || price >= threshold);
      freeText.hidden = !showFree;
      paidText.hidden = showFree;
    }

    updateUnitPrice(unitPrice) {
      this.unitPriceElement.innerText = unitPrice || this.originalUnitPrice;
    }

    updateQuantityLabel(selectedIndex) {
      this.quantityLabels.forEach((label) => {
        label.classList.toggle('current', parseInt(label.dataset.index, 10) === selectedIndex);
      });
    }

    updateQuantityInput(quantity) {
      if (!this.quantityInput) return;
      this.quantityInput.value = quantity;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateProductImage(option) {
      const image = option.querySelector('.bulk-price__option-media img');
      if (!image) return;
      const detail = this.buildHighResImageDetail(image);
      detail.index = option.dataset.index;
      document.dispatchEvent(new CustomEvent('quantity-tiers:image-change', { detail }));
    }

    /* Live rebuilds a high-res candidate set from the option thumbnail's URL
       (pdp.js buildHighResImageDetail): strip the _NxN size suffix, re-emit
       at 550/750/1100/1500. */
    buildHighResImageDetail(image) {
      const src = image.src;
      const alt = image.alt || '';
      if (!src.includes('cdn.shopify.com')) {
        return { imageSrc: src, imageSrcset: image.srcset || '', imageAlt: alt };
      }
      try {
        const [path, query] = src.split('?');
        const suffix = query ? `?${query}` : '';
        const clean = path.replace(/_\d+x\d*\./, '.');
        const extMatch = clean.match(/\.([^.]+)$/);
        if (!extMatch) return { imageSrc: src, imageSrcset: image.srcset || '', imageAlt: alt };
        const ext = `.${extMatch[1]}`;
        const base = clean.replace(/\.[^.]+$/, '');
        const widths = [550, 750, 1100, 1500];
        return {
          imageSrc: `${base}_${widths[widths.length - 1]}x${ext}${suffix}`,
          imageSrcset: widths.map((width) => `${base}_${width}x${ext}${suffix} ${width}w`).join(', '),
          imageAlt: alt,
        };
      } catch (error) {
        return { imageSrc: src, imageSrcset: image.srcset || '', imageAlt: alt };
      }
    }

    parseQuantity(value) {
      const quantity = parseInt(value, 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    /* Live's robust parsers (pdp.js _toMajorUnits/_toCents): tolerate both
       1.234,56 and 1,234.56 shapes as well as plain cent integers. */
    toMajorUnits(value) {
      if (value == null) return NaN;
      let text = String(value).trim();
      if (!text) return NaN;
      text = text.replace(/\s/g, '').replace(/[^0-9,.-]/g, '');
      if (!text) return NaN;
      if (text.includes(',') && text.includes('.')) {
        text =
          text.lastIndexOf(',') > text.lastIndexOf('.')
            ? text.replace(/\./g, '').replace(',', '.')
            : text.replace(/,/g, '');
      } else if (text.includes(',')) {
        text = text.replace(',', '.');
      }
      return parseFloat(text);
    }

    toCents(value) {
      const major = this.toMajorUnits(value);
      if (!Number.isFinite(major)) return 0;
      return /[.,]/.test(String(value ?? '')) ? Math.round(major * 100) : Math.round(major);
    }

    formatWithDelimiters(cents, precision, thousands, decimal) {
      const fixed = (cents / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    formatMoney(cents) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = this.moneyFormat.match(placeholderRegex);
      if (!match) return String(cents);
      let value = '';
      switch (match[1]) {
        case 'amount':
          value = this.formatWithDelimiters(cents, 2, ',', '.');
          break;
        case 'amount_no_decimals':
          value = this.formatWithDelimiters(cents, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = this.formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = this.formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = this.formatWithDelimiters(cents, 0, ' ', '.');
          break;
        default:
          value = this.formatWithDelimiters(cents, 2, ',', '.');
      }
      return this.moneyFormat.replace(placeholderRegex, value);
    }
  }

  customElements.define('quantity-tiers', QuantityTiers);
}
